/**
 * t196u: a task's round checkbox on the phone completes (or reopens) on press-and-hold, not a tap: a ring fills while
 * the finger is down, a light buzz when it's done, and the release never taps what is under it (the long-press
 * ghost-tap fix). A quick tap only shows "Hold to complete" once per person. Keyboard Enter / Space and screen-reader
 * activation complete straight away.
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { HOLD_TO_COMPLETE_MS } from '@tradeoutlet/shared';
import { useUi } from '@/lib/store';
import { swallowNextClick } from './longPress';

export const HOLD_HINT_KEY = 'collab.hint.hold-to-complete';
const TAP_MS = 250;

export function HoldCircle({ done, onDone, label, size = 'md', testId }: { done: boolean; onDone: () => void; label: string; size?: 'sm' | 'md' | 'lg'; testId?: string }) {
  const toast = useUi(s => s.toast);
  const [p, setP] = useState(0);
  const hold = useRef<{ t0: number; raf: number; fired: boolean } | null>(null);
  const cb = useRef(onDone); cb.current = onDone;
  useEffect(() => () => { if (hold.current) cancelAnimationFrame(hold.current.raf); }, []);
  const tick = () => {
    const h = hold.current; if (!h || h.fired) return;
    const f = Math.min(1, (performance.now() - h.t0) / HOLD_TO_COMPLETE_MS);
    setP(f);
    if (f >= 1) { h.fired = true; navigator.vibrate?.(15); swallowNextClick(); cb.current(); setTimeout(() => setP(0), 200); return; }
    h.raf = requestAnimationFrame(tick);
  };
  const start = () => { if (hold.current) cancelAnimationFrame(hold.current.raf); hold.current = { t0: performance.now(), raf: requestAnimationFrame(tick), fired: false }; };
  const end = (cancelled: boolean) => {
    const h = hold.current; hold.current = null;
    if (!h) return;
    cancelAnimationFrame(h.raf);
    if (h.fired) return;
    setP(0);
    if (!cancelled && performance.now() - h.t0 < TAP_MS) {
      swallowNextClick();
      try { if (!localStorage.getItem(HOLD_HINT_KEY)) { localStorage.setItem(HOLD_HINT_KEY, '1'); toast(done ? 'Hold to reopen' : 'Hold to complete', 'info'); } } catch { /* private mode */ }
    }
  };
  const box = size === 'lg' ? 28 : size === 'sm' ? 20 : 24;
  const r = box / 2 - 1.5;
  return (
    <button type="button" role="checkbox" aria-checked={done} aria-label={label} data-testid={testId} data-progress={p ? p.toFixed(2) : undefined}
      aria-description={done ? 'Hold to reopen' : 'Hold to mark as done'}
      onPointerDown={e => { if (e.button === 0) start(); }} onPointerUp={() => end(false)} onPointerCancel={() => end(true)} onPointerLeave={() => end(true)}
      onContextMenu={e => e.preventDefault()}
      // Keyboard and screen readers (click with detail 0) act at once; pointer clicks are handled by the hold.
      onClick={e => { if (e.detail === 0) cb.current(); }}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cb.current(); } }}
      className="no-callout relative flex h-11 w-11 shrink-0 touch-none select-none items-center justify-center">
      <span className={clsx('relative flex items-center justify-center rounded-full border-2', done ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-400 dark:border-slate-500')} style={{ width: box, height: box }}>
        {done && <Check className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} strokeWidth={3} aria-hidden />}
        {p > 0 && !done && <span className="absolute inset-0 rounded-full bg-emerald-600/25" style={{ transform: `scale(${p})` }} aria-hidden />}
      </span>
      {p > 0 && (
        <svg className="pointer-events-none absolute" width={box + 8} height={box + 8} viewBox={`-4 -4 ${box + 8} ${box + 8}`} aria-hidden>
          <circle cx={box / 2} cy={box / 2} r={r + 3} fill="none" stroke={done ? '#64748b' : '#059669'} strokeWidth={3} strokeLinecap="round"
            strokeDasharray={2 * Math.PI * (r + 3)} strokeDashoffset={2 * Math.PI * (r + 3) * (1 - p)} transform={`rotate(-90 ${box / 2} ${box / 2})`} />
        </svg>
      )}
    </button>
  );
}
